<script setup>
import { useI18n } from 'vue-i18n';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import SalesActiveFilters from './SalesActiveFilters.vue';
import SalesAddressFilter from './SalesAddressFilter.vue';
import SalesFilters from './SalesFilters.vue';
import SalesSort from './SalesSort.vue';
import SalesTransactionsTable from './SalesTransactionsTable.vue';

defineProps({
  activeList: { type: String, required: true },
  listTabs: { type: Array, required: true },
  showTabs: { type: Boolean, default: true },
  showManager: { type: Boolean, default: false },
  filters: { type: Object, default: () => ({}) },
  loadingAddresses: { type: Boolean, default: false },
  addresses: { type: Array, default: () => [] },
  sortVisible: { type: Boolean, default: false },
  filtersVisible: { type: Boolean, default: false },
  agents: { type: Array, default: () => [] },
  postSaleStatuses: { type: Array, default: () => [] },
  busyActions: { type: Object, default: () => ({}) },
  error: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  page: { type: Number, default: 1 },
  rows: { type: Array, default: () => [] },
  totalPages: { type: Number, default: 1 },
  totalRows: { type: Number, default: 0 },
});

const emit = defineEmits([
  'selectList',
  'applyAddress',
  'toggleFilters',
  'applySort',
  'applyFilters',
  'clearFilters',
  'removeFilter',
  'archive',
  'changePage',
  'details',
  'deliver',
  'finalize',
  'register',
  'restore',
  'review',
  'signatures',
]);
const { t } = useI18n();
</script>

<template>
  <div class="flex min-w-[390px] flex-col gap-4 sm:min-w-0">
    <LaisTabs
      v-if="showTabs"
      :model-value="activeList"
      :tabs="listTabs"
      :aria-label="t('SALES.ARIA.LISTS')"
      @change="emit('selectList', $event)"
    />
    <SalesAddressFilter
      :filters="filters"
      :loading="loadingAddresses"
      :options="addresses"
      @apply="emit('applyAddress', $event)"
    />
    <SalesFilters
      :active-list="activeList"
      :agents="agents"
      :filters="filters"
      :post-sale-statuses="postSaleStatuses"
      :visible="filtersVisible"
      @apply="emit('applyFilters', $event)"
      @clear="emit('clearFilters')"
    />
    <SalesTransactionsTable
      :active-list="activeList"
      :busy-actions="busyActions"
      :error="error"
      :loading="loading"
      :page="page"
      :rows="rows"
      :show-manager="showManager"
      :total-pages="totalPages"
      :total-rows="totalRows"
      @archive="emit('archive', $event)"
      @change-page="emit('changePage', $event)"
      @details="emit('details', $event)"
      @deliver="emit('deliver', $event)"
      @finalize="emit('finalize', $event)"
      @register="emit('register', $event)"
      @restore="emit('restore', $event)"
      @review="emit('review', $event)"
      @signatures="emit('signatures', $event)"
    >
      <template #toolbar-start>
        <button
          data-test="toggle"
          type="button"
          class="inline-flex min-h-10 items-center gap-2 rounded-[10px] px-2 text-sm font-bold text-lais-text hover:bg-lais-bg-soft"
          @click="emit('toggleFilters')"
        >
          <span
            class="i-lucide-filter size-4 text-lais-primary"
            aria-hidden="true"
          />
          {{ t('SALES.ACTIONS.FILTER') }}
        </button>
        <SalesSort
          v-if="sortVisible"
          inline
          :active-list="activeList"
          :sort="filters.sort"
          @change="emit('applySort', $event)"
        />
      </template>
      <template #active-filters>
        <SalesActiveFilters
          :agents="agents"
          :filters="filters"
          :post-sale-statuses="postSaleStatuses"
          @remove="emit('removeFilter', $event)"
        />
      </template>
    </SalesTransactionsTable>
  </div>
</template>
